'use client';
import { useRouter } from 'next/navigation';
import { useState, useRef, useEffect, JSX } from 'react';
import { useValidator } from '@/hooks';
import useErrorCount from '@/hooks/useErrorCount';
import { VALIDITY, US_STATES } from '@/constants';
import type { Config, ConfigValidations, Fields } from '@/hooks/useValidator/interfaces';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Header } from '@/ui-components/organisms/Header/Header';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Option, OptionGroup, Select } from '@/ui-components/atoms/Inputs/Select';
import { Input, InputType } from '@/ui-components/atoms/Inputs/Input/Input';
import { Checkbox } from '@/ui-components/atoms/Inputs/Checkbox/Checkbox';
import { RadioGroup } from '@/ui-components/atoms/Inputs/RadioGroup/RadioGroup';
import { RadioButton } from '@/ui-components/atoms/Inputs/Radio/Radio';
import { Textarea } from '@/ui-components/atoms/Inputs/Input/Textarea';
import { Form, FormProps } from '@/ui-components/atoms/Inputs/Form/Form';
import { FormErrorDisplay } from '@/ui-components/atoms/Inputs/FormErrorDisplay/FormErrorDisplay';
import submitJson from '@/ui-components/atoms/Inputs/Form/contentTypes/json';
import Ended from '@/components/page/Sweeps/Ended';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { mapChannelCard } from '@/components/molecules/ChannelCard/ChannelDataUtils';
import ChannelCardGroupWrapper from '@/components/molecules/ChannelCardGroup/ChannelCardGroupWrapper';
import styles from './flexibleForm.module.scss';

const {
    leftAlignedHeaderStyles,
    descriptionTextStyles,
    flexForm,
    halfWidth,
    fullWidth,
    formSectionHeaderStyles,
    dropdownSelectStyles,
    submitButtonWrapperCentered,
    submitButtonWrapperRight,
    successHeadlineStyles,
    successErrorWrapper,
    channelCardsWrapper,
    wrapperRow,
    stateContainer,
    stateWrapper,
    zipInputStyles,
    subheadlineStyles,
    ctaStyles,
    duplicationFormError,
    duplicationFormErrorCentered,
} = styles;

interface ServerFields {
    error: string;
    message: string;
    name: string;
}

type ValidationConfig = Record<string, Array<ConfigValidations>>;
type SubmitState = 'untouched' | 'failed' | 'success' | 'error' | 'duplicate';

interface ClientFlexibleFormProps {
    asset: SiteFlexibleForm;
}

export function SweepsFlexibleForm({ asset }: ClientFlexibleFormProps) {
    const { fields } = asset;
    const {
        headline,
        description,
        form_fields,
        error_headline,
        error_subheadline,
        success_headline,
        success_subheadline,
        presentation,
        hidden_form_fields,
        cta,
        sweeps,
        error_cta,
        success_page_redirect,
    } = fields;

    const router = useRouter();
    const presentationStyles = presentation?.styles;
    const isCenter = presentationStyles?.alignment === 'Centre';
    const emailRef = useRef<HTMLInputElement>(null);
    const confirmEmailRef = useRef<HTMLInputElement>(null);
    const ctaFields = cta?.fields;

    const hiddenFields = hidden_form_fields?.map((hiddenField, index) => {
        return <input key={index} type="hidden" name={hiddenField.name} value={hiddenField.value} />;
    });

    function createValidationConfigFields(formFields: SiteFormField[]) {
        const fields: ValidationConfig = {};
        formFields.forEach((formField: SiteFormField) => {
            const helperText = <RenderRTE contentfulDoc={formField.fields.error_message} />;
            const fieldName = formField.fields.field_name;
            const fieldType = formField.fields.type.toLowerCase();
            const required = formField.fields.required;

            if (fieldType === 'header') {
                return;
            }

            if (fieldType === 'email only' && required) {
                fields[fieldName] = [
                    { type: 'email', helperText: helperText, validateAlso: [confirmEmailRef.current] },
                ];
            } else if (fieldType === 'email/confirmation' && required) {
                fields[fieldName] = [
                    { type: 'matchesCaseInsensitive', helperText: helperText, config: emailRef.current },
                ];
            } else if (fieldType === 'phone' && required) {
                fields[fieldName] = [{ type: 'minlength', config: 14, helperText: helperText }];
            } else if (fieldType === 'checkbox' && required) {
                fields[fieldName] = [{ type: 'istrue', helperText: helperText }];
            } else if (fieldType === 'radio button' && required) {
                fields[fieldName] = [{ type: 'istrue', helperText: helperText }];
            } else if (required) {
                fields[fieldName] = [{ type: 'required', helperText: helperText }];
            }
        });

        fields['zip'] = [{ type: 'minlength', config: 5, helperText: <p>{'Valid zip code required'}</p> }];

        return fields;
    }

    const validationConfig: Config = {
        fields: createValidationConfigFields([...form_fields]),
    };

    const validator = useValidator(validationConfig);
    const [validations, setValidations] = useState(validator.getDefault());
    const [submitState, setSubmitState] = useState<SubmitState>('untouched');
    const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
    const [ended, setEnded] = useState(false);
    const errorCount: number = useErrorCount(validations);

    useEffect(() => {
        const browserWindow = window as BrowserWindow;

        if (browserWindow._sxmDataLayerPush) {
            if (submitState === 'success') {
                browserWindow._sxmDataLayerPush('sweeps-success');
            } else if (submitState === 'failed') {
                browserWindow._sxmDataLayerPush('sweeps-fail');
            }
        }
    }, [submitState]);

    const sweepsFields = sweeps?.fields;
    if (ended && sweepsFields) {
        const endedFields = {
            closed_headline: sweepsFields.closed_headline,
            closed_description: sweepsFields.closed_description,
            closed_message: sweepsFields.closed_message,
        };
        return <Ended sweepsFields={endedFields} asW2 />;
    }

    function validateInput(input: HTMLInputElement) {
        const newValidations = validator.validateField(input);
        const updatedValidations = { ...validations, ...newValidations };
        setValidations(updatedValidations);

        return updatedValidations.form.invalid === VALIDITY.valid;
    }

    function validateForm(event: React.SyntheticEvent) {
        const newValidations = validator.validate(event);
        const updatedValidations = { ...validations, ...newValidations };

        setValidations(updatedValidations);
        return updatedValidations.form.invalid === VALIDITY.valid;
    }

    const formType = 'sweeps';
    const formFieldElements = form_fields.map((formField: SiteFormField) => {
        const {
            cms_id,
            fields: { field_name, aria_label, required, type, dropdown_radio_button_values, label, width },
        } = formField;
        const fieldType = type.toLowerCase();
        const fieldName = field_name;
        const fieldLabel = <RenderRTE contentfulDoc={label} />;

        if (fieldType === 'state') {
            return (
                <div key={cms_id} className={stateWrapper}>
                    <Grid container className={stateContainer}>
                        <Grid col={{ sm: 6 }}>
                            <Select
                                label={fieldLabel}
                                type="dropdown"
                                theme="grayLightWeb2"
                                name={fieldName}
                                invalid={validations[fieldName].invalid}
                                invalidMessage={validations[fieldName].helperText.required}
                                onBlur={(event) => validateInput(event.currentTarget)}
                                required
                                dataCy={`${formType}-form-select-state`}
                                errorDataCy={`${formType}-form-select-state-err`}
                                selectHeader={{
                                    title: fieldLabel,
                                    body: '',
                                }}
                            >
                                <OptionGroup>
                                    {US_STATES.map((state) => {
                                        return (
                                            <Option key={state.abbreviation} value={state.abbreviation}>
                                                {state.name}
                                            </Option>
                                        );
                                    })}
                                </OptionGroup>
                            </Select>
                        </Grid>
                        <Grid col={{ sm: 6 }}>
                            <Input
                                key={cms_id}
                                className={zipInputStyles}
                                type="zip"
                                name="zip"
                                label="Zip Code"
                                invalid={validations.zip.invalid}
                                required
                                onBlur={(event) => validateInput(event.currentTarget)}
                                invalidMessage={validations.zip.helperText.minlength}
                                dataCy={`${formType}-form-zip`}
                                errorDataCy={`${formType}-form-zip-err`}
                                noSpacing
                            />
                        </Grid>
                    </Grid>
                </div>
            );
        } else if (fieldType === 'header') {
            return <RenderRTE contentfulDoc={label} key={cms_id} className={formSectionHeaderStyles} />;
        } else if (fieldType === 'dropdown' || fieldType === 'nfl favorite team') {
            return (
                <Select
                    key={cms_id}
                    className={dropdownSelectStyles}
                    label={fieldLabel}
                    type="dropdown"
                    theme="grayLightWeb2"
                    name={fieldName}
                    invalid={validations[fieldName]?.invalid}
                    invalidMessage={validations[fieldName]?.helperText?.required}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    required={required}
                    dataCy={`${formType}-form-select-${fieldName}`}
                    errorDataCy={`${formType}-form-select-${fieldName}-err`}
                    selectHeader={{
                        title: fieldLabel,
                        body: '',
                    }}
                >
                    <OptionGroup>
                        {dropdown_radio_button_values?.map((option) => {
                            return (
                                <Option key={option.id} value={option.key}>
                                    {option.value}
                                </Option>
                            );
                        })}
                    </OptionGroup>
                </Select>
            );
        } else if (fieldType === 'checkbox') {
            return (
                <Checkbox
                    key={cms_id}
                    name={fieldName}
                    value="Y"
                    invalid={validations[fieldName]?.invalid}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    invalidMessage={validations[fieldName]?.helperText.istrue}
                    dataCy={`${formType}-form-checkbox-${fieldName}`}
                    required={required}
                >
                    <Text tag="div" variant="caption" style={{ color: 'var(--base-contrast-subtle)' }}>
                        {fieldLabel}
                    </Text>
                </Checkbox>
            );
        } else if (fieldType === 'radio button') {
            return (
                <RadioGroup
                    key={cms_id}
                    theme="themeGrey"
                    legend={label && <RenderRTE contentfulDoc={label} />}
                    invalidMessage={validations[fieldName]?.helperText.istrue}
                    invalid={validations[fieldName]?.invalid}
                >
                    {dropdown_radio_button_values?.map((element, index) => {
                        return (
                            <RadioButton
                                key={element.id}
                                label={element.value}
                                value={element.value}
                                name={fieldName}
                                dataCy={`${formType}-form-radio-answer${index}`}
                            />
                        );
                    })}
                </RadioGroup>
            );
        } else if (fieldType === 'text area') {
            return (
                <Textarea
                    key={cms_id}
                    className={width === '50%' ? halfWidth : fullWidth}
                    name={fieldName}
                    label={fieldLabel || ''}
                    showCount
                    invalidMessage={validations[fieldName]?.helperText.required}
                    required={required}
                    onBlur={(event: React.FocusEvent<HTMLTextAreaElement, Element>) =>
                        validateInput(event.currentTarget as unknown as HTMLInputElement)
                    }
                    invalid={validations[fieldName]?.invalid}
                />
            );
        } else {
            const messageType = validationConfig.fields[fieldName]?.[0].type;
            const invalidMessage = validations[fieldName]?.helperText[messageType];

            return (
                <Input
                    wrapperId={width === '50%' ? 'halfWidth' : 'fullWidth'}
                    key={cms_id}
                    {...(fieldType === 'email only'
                        ? { ref: emailRef }
                        : fieldType === 'email/confirmation'
                          ? { ref: confirmEmailRef }
                          : {})}
                    type={fieldType === 'phone' ? 'tel' : (fieldType as InputType)}
                    name={fieldName}
                    label={fieldLabel}
                    invalid={validations[fieldName]?.invalid}
                    required={required}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    invalidMessage={invalidMessage}
                    dataCy={`${formType}-form-${fieldName}`}
                    errorDataCy={`${formType}-form-${fieldName}-err`}
                    aria-label={aria_label}
                    inputWrapperStyles={width === '50%' ? halfWidth : fullWidth}
                />
            );
        }
    });

    function onSubmit(event: React.SyntheticEvent, data: FormData) {
        setSubmitAttempted(true);
        const isValid = validateForm(event);

        if (isValid) {
            if (data.get('nfl_optin')) {
                const nflFormData = new FormData();
                nflFormData.set('languagePreference', 'en-US');
                nflFormData.set('sourceName', 'DE_Sweepstakes');
                nflFormData.set('email', data.get('email') as string);
                nflFormData.set('firstName', data.get('firstName') as string);
                nflFormData.set('lastName', data.get('lastName') as string);
                nflFormData.set('streetAddress', data.get('address1') as string);
                nflFormData.set('city', data.get('city') as string);
                nflFormData.set('state', data.get('state') as string);
                nflFormData.set('zip', data.get('zip') as string);
                nflFormData.set('favoriteTeam', data.get('favoriteTeam') as string);

                submitJson('/phx/services/slam/utility/nfl-opt-in', 'POST', nflFormData);
            }
        }

        return isValid;
    }

    function onFail() {
        setSubmitState('failed');
    }

    function renderSubmitMessage() {
        const success = submitState === 'success';
        const failed = submitState === 'failed';

        if (success && success_page_redirect) {
            router.push(success_page_redirect.fields.href);
        }

        let featuredChannelsData = null;
        if (success && sweeps?.fields.featured_channels) {
            const featuredChannels = mapChannelCard(sweeps.fields.featured_channels);
            const [channelIds, channelCards] = featuredChannels;
            featuredChannelsData = channelCards?.length > 0 && (
                <div className={channelCardsWrapper}>
                    <ChannelCardGroupWrapper
                        channels={channelCards}
                        channelIds={channelIds}
                        title={sweeps?.fields.channel_card_intro_text}
                        link={sweeps?.fields.channel_group_link}
                    />
                </div>
            );
        }

        const errorCTA = error_cta?.fields;

        return (
            <div className={successErrorWrapper}>
                {success && (
                    <ImageWrapper
                        src="https://www.siriusxm.com/content/dam/sxm-com/lottie/stella/Stella-Wag-Animation.gif"
                        alt="Stella Alert"
                        optimizedWidth={100}
                        optimizedHeight={100}
                        loadingStrategy="eager"
                        skipOptimization={true}
                    />
                )}
                <div data-cy="sweeps-form-submit-success-message" className={successHeadlineStyles}>
                    <Text tag="h5" variant="h5">
                        {success && success_headline}
                        {failed && error_headline}
                    </Text>
                </div>
                <Text tag="p" className={subheadlineStyles}>
                    {success && <RenderRTE contentfulDoc={success_subheadline} />}
                    {failed && <RenderRTE contentfulDoc={error_subheadline} />}
                </Text>
                {failed && errorCTA && (
                    <Button
                        type="button"
                        className={ctaStyles}
                        href={errorCTA.href}
                        linkTarget={errorCTA.link_target}
                        ariaLabel={errorCTA.name}
                        variant={errorCTA.theme}
                        size={errorCTA.size.toLowerCase()}
                        fitting="hug-contents"
                        dataCy="sweeps-form-error-cta"
                    >
                        {errorCTA.link_text}
                    </Button>
                )}
                {featuredChannelsData}
            </div>
        );
    }

    async function onSuccess(response: Response) {
        const json = await response.json();

        if (json.formSubmitResponse.status.message === 'ERROR') {
            const serverValidations: Fields = {};

            json.formSubmitResponse.errors.fields.forEach((field: ServerFields) => {
                serverValidations[field.name] = {
                    value: validations[field.name].value,
                    invalid: VALIDITY.invalid,
                    validations: { ...validations[field.name].validations },
                    helperText: { ...validations[field.name].helperText },
                };

                if (field.name === 'form') {
                    if (field.error === 'unknown') {
                        setSubmitState('failed');
                    } else if (field.error === 'invalid') {
                        if (field.message === 'duplicate') {
                            serverValidations.form = {
                                value: '',
                                invalid: VALIDITY.invalid,
                                validations: { formerror: true },
                                helperText: { formerror: 'Sorry, only one entry per person allowed.' },
                            };

                            setSubmitState('duplicate');
                        } else if (field.message === 'ended') {
                            setEnded(true);
                        }
                    }
                } else {
                    delete serverValidations.form;
                }
            });

            setValidations({ ...validations, ...serverValidations });
        } else if (json.formSubmitResponse.status.message === 'SUCCESS') {
            setSubmitState('success');
        }
    }

    function beforeSubmit(data: FormData) {
        const phone = data.get('phone') as string;
        data.set('phone', phone.replace(/\D/g, ''));

        const zip = data.get('zip') as string;
        data.set('zip', zip.replace(/\D/g, '').substring(0, 5));

        data.set('languagePreference', 'en-US');
        data.set('sourceName', 'DE_Sweepstakes');

        const state = data.get('state') as string;
        data.set('state', state.trim());

        return data;
    }

    function wrapElements(elements: JSX.Element[]) {
        const result = [];

        for (let i = 0; i < elements.length; i++) {
            const current = elements[i];
            const next = elements[i + 1];

            if (current.props?.wrapperId === 'halfWidth' && next?.props?.wrapperId === 'halfWidth') {
                result.push(
                    <div className={wrapperRow} key={`wrap-${i}`}>
                        {current}
                        {next}
                    </div>,
                );
                i++;
            } else {
                result.push(current);
            }
        }

        return result;
    }

    const content = isCenter ? (
        <>
            <StyledContainer standardMoleculeSpacing>
                <Header
                    headingTag="h2"
                    heading={<RenderRTE contentfulDoc={headline} />}
                    subhead={<RenderRTE contentfulDoc={description} />}
                />
            </StyledContainer>
            <Grid container>
                <Grid col={{ sm: 12, md: 6, lg: 6 }} start={{ md: 4, lg: 4 }}>
                    <FormErrorDisplay errorCount={submitAttempted ? errorCount : 0} />
                </Grid>
                <Grid col={{ sm: 12, md: 6, lg: 6 }} start={{ md: 4, lg: 4 }}>
                    <div>
                        <div className={flexForm}>{wrapElements(formFieldElements)}</div>
                        <div className={submitButtonWrapperCentered}>
                            {validations.form && validations.form.validations.formerror && (
                                <StyledContainer>
                                    <Text tag="div" className={duplicationFormErrorCentered} role="alert">
                                        {validations.form.helperText.formerror}
                                    </Text>
                                </StyledContainer>
                            )}
                            <Button
                                type="submit"
                                ariaLabel="register for sweeps"
                                variant={ctaFields?.theme}
                                size={ctaFields?.size.toLowerCase()}
                                fitting="hug-contents"
                                dataCy="sweeps-form-submit-button"
                            >
                                {ctaFields?.link_text}
                            </Button>
                        </div>
                    </div>
                </Grid>
            </Grid>
        </>
    ) : (
        <Grid container>
            <Grid col={{ sm: 12, md: 6 }} standardMoleculeSpacing>
                <Header
                    headingTag="h2"
                    heading={<RenderRTE contentfulDoc={headline} />}
                    theme="left"
                    className={leftAlignedHeaderStyles}
                />
            </Grid>
            <Grid col={{ sm: 12, md: 6 }}>
                <Text tag="p" className={descriptionTextStyles}>
                    <RenderRTE contentfulDoc={description} />
                </Text>
                <Grid col={{ sm: 12, lg: 6 }}>
                    <div className={flexForm}>{wrapElements(formFieldElements)}</div>
                </Grid>
                <div className={submitButtonWrapperRight}>
                    {validations.form && validations.form.validations.formerror && (
                        <StyledContainer>
                            <Text tag="div" className={duplicationFormError} role="alert">
                                {validations.form.helperText.formerror}
                            </Text>
                        </StyledContainer>
                    )}
                    <Button
                        type="submit"
                        ariaLabel="register for sweeps"
                        variant={ctaFields?.theme}
                        size={ctaFields?.size.toLowerCase()}
                        fitting="hug-contents"
                        dataCy="sweeps-form-submit-button"
                    >
                        {ctaFields?.link_text}
                    </Button>
                </div>
            </Grid>
        </Grid>
    );

    return (
        <>
            <div aria-live="polite">{renderSubmitMessage()}</div>
            {submitState === 'untouched' || submitState === 'duplicate' ? (
                <Form
                    action={ctaFields?.href}
                    method="POST"
                    contentType={asset.fields.content_type as FormProps['contentType']}
                    onSubmit={onSubmit}
                    onFail={onFail}
                    onSuccess={onSuccess}
                    onBeforeSubmit={beforeSubmit}
                    onChange={validateForm}
                    novalidate={true}
                    dataCy="sweeps-form-form"
                >
                    {hiddenFields}
                    {content}
                </Form>
            ) : undefined}
        </>
    );
}
